import { useState, useEffect } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

const gentleRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const floatUp = keyframes`
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-8px) scale(1.02); }
`;

const fadeInScale = keyframes`
  0% { 
    opacity: 0; 
    transform: scale(0.8) translateY(20px);
  }
  100% { 
    opacity: 1; 
    transform: scale(1) translateY(0);
  }
`;

const slideInUp = keyframes`
  0% { 
    opacity: 0; 
    transform: translateY(30px);
  }
  100% { 
    opacity: 1; 
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% { 
    opacity: 0.6;
    transform: scale(1);
  }
  50% { 
    opacity: 1;
    transform: scale(1.05);
  }
`;

const RotatingImage = styled('img')`
  width: 100%;
  height: 100%;
  object-fit: contain;
  animation: ${gentleRotate} 30s cubic-bezier(0.4, 0, 0.2, 1) infinite;
  position: absolute;
  transform: translate(-50%, -50%);
  transform-origin: center center;
  filter: drop-shadow(0 4px 20px rgba(255, 255, 255, 0.1));
`;

const LogoContainer = styled(Box)`
  filter: drop-shadow(0 8px 32px rgba(255, 255, 255, 0.15));
`;

interface SplashScreenProps {
    innerLogoSrc: string;
    outerCircleSrc: string;
    schoolName?: string;
    scrollFadeThreshold?: number;
    onClose?: () => void;
}

const SplashScreen: React.FC<SplashScreenProps> = ({
    innerLogoSrc,
    outerCircleSrc,
    schoolName = "Ever Green Sr. Sec. School",
    scrollFadeThreshold = 50,
    onClose,
}) => {
    const theme = useTheme();
    const [scrollY, setScrollY] = useState(0);
    const [opacity, setOpacity] = useState(1);
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        let scrollTimer: NodeJS.Timeout;
        let scrollPosition = 0;
        
        // Lock background scroll when splash is visible
        if (isVisible) {
            scrollPosition = window.scrollY;
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollPosition}px`;
            document.body.style.width = '100%';
        }
        
        const handleWheel = (e: WheelEvent) => {
            if (!isVisible) return;
            
            e.preventDefault();
            
            // Simulate scroll accumulation for fade effect
            scrollPosition += e.deltaY * 0.5; // Adjust sensitivity
            scrollPosition = Math.max(0, scrollPosition); // Prevent negative scroll
            
            const currentScrollY = scrollPosition;
            setScrollY(scrollY);
            setScrollY(currentScrollY);

            // Calculate opacity based on accumulated scroll
            const newOpacity = Math.max(0, 1 - (currentScrollY / scrollFadeThreshold));
            setOpacity(newOpacity);

            // Debounce the visibility change to prevent flickering
            clearTimeout(scrollTimer);
            scrollTimer = setTimeout(() => {
                if (newOpacity <= 0 && isVisible) {
                    setIsVisible(false);
                    // Restore scroll position and enable scrolling
                    document.body.style.overflow = '';
                    document.body.style.position = '';
                    document.body.style.top = '';
                    document.body.style.width = '';
                    window.scrollTo(0, scrollPosition);
                    onClose?.();
                }
            }, 50);
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (!isVisible) return;
            e.preventDefault();
        };

        // Add wheel and touch event listeners to capture scroll attempts
        if (isVisible) {
            window.addEventListener('wheel', handleWheel, { passive: false });
            window.addEventListener('touchmove', handleTouchMove, { passive: false });
        }

        // Cleanup function
        return () => {
            window.removeEventListener('wheel', handleWheel);
            window.removeEventListener('touchmove', handleTouchMove);
            clearTimeout(scrollTimer);
            
            // Restore scroll on cleanup
            if (isVisible) {
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
            }
        };
    }, [scrollFadeThreshold, isVisible, onClose]);

    // Don't render if not visible
    if (!isVisible) {
        return null;
    }

    return (
        <Box
            className="fixed inset-0 z-[1000] flex flex-col items-center justify-center"
            sx={{
                opacity,
                transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
                backdropFilter: 'blur(24px)',
                pointerEvents: opacity > 0 ? 'auto' : 'none',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `
                        radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 40% 40%, rgba(120, 200, 255, 0.05) 0%, transparent 50%)
                    `,
                    pointerEvents: 'none',
                }
            }}
        >
            {/* Main Logo Container */}
            <Box
                sx={{
                    position: 'relative',
                    width: 320,
                    height: 320,
                    margin: '0 auto',
                    animation: `${fadeInScale} 1.2s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                }}
            >
                {/* Outer rotating circle */}
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: 'linear-gradient(45deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
                        padding: '8px',
                        boxShadow: `
                            0 0 50px rgba(255, 255, 255, 0.1),
                            inset 0 0 50px rgba(255, 255, 255, 0.05)
                        `,
                    }}
                >
                    <RotatingImage
                        src={outerCircleSrc}
                        alt="Rotating Outer Circle"
                        style={{
                            borderRadius: '50%',
                        }}
                    />
                </Box>

                {/* Inner logo */}
                <LogoContainer
                    sx={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        left: '2.3%',
                        top: '3.3%',
                        // transform: 'translate(-50%, -50%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        // borderRadius: '50%',
                        // // background: 'rgba(255, 255, 255, 0.95)',
                        // backdropFilter: 'blur(10px)',
                        // border: '2px solid rgba(255, 255, 255, 0.2)',
                        // boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    <img
                        src={innerLogoSrc}
                        alt="School Logo"
                        style={{
                            width: '100%',
                            height: '100%',
                            
                            objectFit: 'contain',
                            position: 'relative',
                            zIndex: 2,
                        }}
                    />
                </LogoContainer>

                {/* Subtle glow rings */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '-20px',
                        left: '-20px',
                        right: '-20px',
                        bottom: '-20px',
                        borderRadius: '50%',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        animation: `${pulse} 3s ease-in-out infinite`,
                        pointerEvents: 'none',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: '-40px',
                        left: '-40px',
                        right: '-40px',
                        bottom: '-40px',
                        borderRadius: '50%',
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        animation: `${pulse} 3s ease-in-out infinite 1.5s`,
                        pointerEvents: 'none',
                    }}
                />
            </Box>

            {/* School Name */}
            <Typography
                variant="h3"
                component="h1"
                sx={{
                    mt: 6,
                    fontWeight: 300,
                    fontSize: '2.5rem',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontFamily: '"Inter", "Roboto", sans-serif',
                    textAlign: 'center',
                    letterSpacing: '2px',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.8) 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    animation: `${slideInUp} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                    animationDelay: '0.5s',
                    opacity: 0,
                    textShadow: 'none',
                }}
            >
                {schoolName}
            </Typography>

            {/* Subtitle */}
            <Typography
                variant="h6"
                sx={{
                    mt: 2,
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontFamily: '"Inter", "Roboto", sans-serif',
                    fontWeight: 400,
                    fontSize: '1.1rem',
                    letterSpacing: '1px',
                    textAlign: 'center',
                    animation: `${slideInUp} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                    animationDelay: '0.7s',
                    opacity: 0,
                }}
            >
                Excellence in Education Since 1985
            </Typography>

            {/* Minimalist scroll indicator */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: 40,
                    // left: '50%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    animation: `${slideInUp} 1s cubic-bezier(0.4, 0, 0.2, 1) forwards`,
                    animationDelay: '1s',
                    opacity: 0,
                }}
            >
                <Typography 
                    variant="caption" 
                    sx={{ 
                        mb: 2,
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.85rem',
                        fontWeight: 300,
                        letterSpacing: '0.5px',
                        textAlign: 'center',
                        fontFamily: '"Inter", "Roboto", sans-serif',
                    }}
                >
                    Scroll to explore
                </Typography>
                
                {/* Modern scroll indicator */}
                <Box
                    sx={{
                        width: '2px',
                        height: '30px',
                        backgroundColor: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '1px',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            borderRadius: '1px',
                            animation: `${keyframes`
                                0% { transform: translateY(-8px); opacity: 0; }
                                50% { opacity: 1; }
                                100% { transform: translateY(30px); opacity: 0; }
                            `} 2s ease-in-out infinite`,
                        }
                    }}
                />
            </Box>

            {/* Subtle background particles */}
            {[...Array(6)].map((_, i) => (
                <Box
                    key={i}
                    sx={{
                        position: 'absolute',
                        width: `${Math.random() * 4 + 2}px`,
                        height: `${Math.random() * 4 + 2}px`,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%',
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        animation: `${floatUp} ${Math.random() * 3 + 4}s ease-in-out infinite`,
                        animationDelay: `${Math.random() * 2}s`,
                        pointerEvents: 'none',
                    }}
                />
            ))}
        </Box>
    );
};

export default SplashScreen;